import { escapeHtml } from "../lib/format.js";
import { withButtonLoading } from "../lib/dom.js";
import { buildTextarea, buildTextInput } from "../widgets/form-controls.js";
import { mountDateInput } from "../widgets/date-input.js";

/**
 * Show a branded confirmation modal and return a Promise<boolean>.
 * Resolves true if the user confirms, false if they cancel or press Escape.
 */
export function showConfirmModal({ title = "Are you sure?", message = "", confirmLabel = "Confirm", cancelLabel = "Cancel", danger = false } = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "ar-confirm-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "ar-confirm-modal-title");
    overlay.innerHTML = `
      <div class="ar-confirm-modal">
        <h2 class="ar-confirm-modal-title" id="ar-confirm-modal-title">${escapeHtml(title)}</h2>
        ${message ? `<p class="ar-confirm-modal-message">${escapeHtml(message)}</p>` : ""}
        <div class="ar-confirm-modal-actions">
          <button class="ar-btn ar-btn-secondary" id="ar-confirm-modal-cancel" type="button">${escapeHtml(cancelLabel)}</button>
          <button class="ar-btn ${danger ? "ar-btn-danger-primary" : "ar-btn-primary"}" id="ar-confirm-modal-confirm" type="button">${escapeHtml(confirmLabel)}</button>
        </div>
      </div>
    `;

    function close(result) {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve(result);
    }

    function onKey(e) {
      if (e.key === "Escape") close(false);
    }

    overlay.querySelector("#ar-confirm-modal-cancel").addEventListener("click", () => close(false));
    overlay.querySelector("#ar-confirm-modal-confirm").addEventListener("click", () => close(true));
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(false); });
    document.addEventListener("keydown", onKey);

    document.body.appendChild(overlay);
    overlay.querySelector("#ar-confirm-modal-confirm").focus();
  });
}

/**
 * Single-action alert modal — informational, one OK button, no Cancel path.
 * The DS Alert component (the replacement for native alert()). Title-only by
 * default; pass `message` for an optional detail line. Resolves when dismissed
 * (OK / Enter / Escape / backdrop click).
 */
export function showAlertModal({ title = "", message = "", confirmLabel = "OK" } = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "ar-confirm-modal-overlay";
    overlay.setAttribute("role", "alertdialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "ar-alert-modal-title");
    overlay.innerHTML = `
      <div class="ar-confirm-modal">
        <h2 class="ar-confirm-modal-title" id="ar-alert-modal-title">${escapeHtml(title)}</h2>
        ${message ? `<p class="ar-confirm-modal-message">${escapeHtml(message)}</p>` : ""}
        <div class="ar-confirm-modal-actions">
          <button class="ar-btn ar-btn-primary" id="ar-alert-modal-ok" type="button">${escapeHtml(confirmLabel)}</button>
        </div>
      </div>
    `;

    function close() {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve();
    }
    function onKey(e) { if (e.key === "Escape" || e.key === "Enter") close(); }

    overlay.querySelector("#ar-alert-modal-ok").addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
    document.addEventListener("keydown", onKey);

    document.body.appendChild(overlay);
    overlay.querySelector("#ar-alert-modal-ok").focus();
  });
}

/**
 * One choice from a list, as the DS "Radio with description": a title per
 * option and an optional line beneath it. Resolves with the chosen value, or
 * null on Cancel / Escape / the backdrop. Nothing is chosen unless `value`
 * names an option, and the confirm button waits for a choice, so a choice
 * that matters (which set of books) is never made by default.
 *
 * With `search`, a field above the options asks `search.find(text)` for more
 * of them (as many as it answers, below the fixed `options`), after a pause in
 * typing (`search.delay`, 300ms unless said). `search.empty` is said when it
 * finds none, and a search that fails says the error's own message (else
 * `search.failed`) in the same place. With `search.browse`, it asks
 * `find("")` as it opens, so the whole list is there to scroll before
 * anything is typed, and the field narrows it: one control, browse or search
 * (the books' contacts, #951). `search.loading` is said while it asks.
 */
export function showChoiceModal({ title = "", message = "", options = [], value = null, confirmLabel = "Continue", cancelLabel = "Cancel", search = null } = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "ar-confirm-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "ar-choice-modal-title");
    const optionHtml = (o) => `
      <label class="ar-radio-option">
        <input type="radio" class="ar-radio" name="ar-choice" value="${escapeHtml(o.value)}"${o.value === value ? " checked" : ""} />
        <span class="ar-radio-option-text">
          <span class="ar-radio-option-title">${escapeHtml(o.title)}</span>
          ${o.description ? `<span class="ar-radio-option-desc">${escapeHtml(o.description)}</span>` : ""}
        </span>
      </label>`;
    overlay.innerHTML = `
      <div class="ar-confirm-modal">
        <h2 class="ar-confirm-modal-title" id="ar-choice-modal-title">${escapeHtml(title)}</h2>
        ${message ? `<p class="ar-confirm-modal-message">${escapeHtml(message)}</p>` : ""}
        ${search ? buildTextInput({ label: search.label, name: "ar-choice-search", type: "search", error: false, widthModifier: "full", attrs: { autocomplete: "off" } }) : ""}
        <fieldset class="ar-choice-modal-options${search?.browse ? " ar-choice-modal-options-browse" : ""}" aria-label="${escapeHtml(title)}">
          ${options.map(optionHtml).join("")}
          <div class="ar-choice-modal-found" data-choice-found></div>
        </fieldset>
        <p class="ar-input-spec-hint" data-choice-empty aria-live="polite"${search?.browse ? "" : " hidden"}>${escapeHtml(search?.browse ? search.loading || "Loading…" : search?.empty || "")}</p>
        <div class="ar-confirm-modal-actions">
          <button class="ar-btn ar-btn-secondary" id="ar-choice-modal-cancel" type="button">${escapeHtml(cancelLabel)}</button>
          <button class="ar-btn ar-btn-primary" id="ar-choice-modal-confirm" type="button"${value ? "" : " disabled"}>${escapeHtml(confirmLabel)}</button>
        </div>
      </div>
    `;
    const confirmBtn = overlay.querySelector("#ar-choice-modal-confirm");
    const chosen = () => overlay.querySelector('input[name="ar-choice"]:checked')?.value ?? null;

    function close(result) {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve(result);
    }
    function onKey(e) {
      if (e.key === "Escape") close(null);
    }

    overlay.addEventListener("change", (e) => {
      if (e.target?.name === "ar-choice") confirmBtn.disabled = chosen() == null;
    });
    overlay.querySelector("#ar-choice-modal-cancel").addEventListener("click", () => close(null));
    confirmBtn.addEventListener("click", () => close(chosen()));
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(null); });
    document.addEventListener("keydown", onKey);

    if (search) {
      const field = overlay.querySelector('input[name="ar-choice-search"]');
      const found = overlay.querySelector("[data-choice-found]");
      const empty = overlay.querySelector("[data-choice-empty]");
      let timer = null;
      let asked = 0;
      const ask = async () => {
        const text = field.value.trim();
        const mine = ++asked;
        if (!text && !search.browse) {
          found.innerHTML = "";
          empty.hidden = true;
          return;
        }
        let more = [];
        let failure = null;
        try {
          more = await search.find(text);
        } catch (err) {
          // Said as it is: "No contacts found" for a search that never ran
          // would send somebody to add a contact the books already have.
          failure = err?.message || search.failed || "Couldn’t search. Please try again.";
        }
        // A slower answer to an older search is dropped.
        if (mine !== asked) return;
        const keep = chosen();
        found.innerHTML = more.map(optionHtml).join("");
        empty.textContent = failure || search.empty || "";
        empty.hidden = !failure && more.length > 0;
        const again = keep && overlay.querySelector(`input[name="ar-choice"][value="${CSS.escape(keep)}"]`);
        if (again) again.checked = true;
        confirmBtn.disabled = chosen() == null;
      };
      field.addEventListener("input", () => {
        clearTimeout(timer);
        timer = setTimeout(ask, search.delay ?? 300);
      });
      if (search.browse) ask();
    }

    document.body.appendChild(overlay);
    (overlay.querySelector('input[name="ar-choice-search"]') || overlay.querySelector('input[name="ar-choice"]') || confirmBtn).focus();
  });
}

/**
 * Confirmation modal with an optional free-text note — for actions worth a
 * word of context (e.g. recording why a client declined a quote). The note is
 * optional, so the confirm button is always enabled. If `onSubmit(note)` is
 * provided it runs inside the button's loading state; a throw is shown inline
 * and the modal stays open for a retry. Resolves true once confirmed (and
 * onSubmit succeeds), false on cancel / Escape / backdrop.
 */
export function showNotePromptModal({
  title = "Are you sure?",
  message = "",
  noteLabel = "Add a note (optional)",
  placeholder = "",
  maxLength = 1000,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  onSubmit
} = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "ar-confirm-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "ar-note-prompt-title");
    overlay.innerHTML = `
      <div class="ar-confirm-modal">
        <h2 class="ar-confirm-modal-title" id="ar-note-prompt-title">${escapeHtml(title)}</h2>
        ${message ? `<p class="ar-confirm-modal-message">${escapeHtml(message)}</p>` : ""}
        <form id="ar-note-prompt-form" novalidate>
          ${buildTextarea({ label: noteLabel, name: "note", placeholder, rows: 3, error: false, widthModifier: "full" })}
          <p class="ar-input-spec-error-text" id="ar-note-prompt-error" hidden></p>
          <div class="ar-confirm-modal-actions">
            <button class="ar-btn ar-btn-secondary" id="ar-note-prompt-cancel" type="button">${escapeHtml(cancelLabel)}</button>
            <button class="ar-btn ${danger ? "ar-btn-danger-primary" : "ar-btn-primary"}" id="ar-note-prompt-confirm" type="submit">${escapeHtml(confirmLabel)}</button>
          </div>
        </form>
      </div>
    `;

    function close(result) {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve(result);
    }

    function onKey(e) {
      if (e.key === "Escape") close(false);
    }

    const form = overlay.querySelector("#ar-note-prompt-form");
    const errorEl = overlay.querySelector("#ar-note-prompt-error");
    const confirmBtn = overlay.querySelector("#ar-note-prompt-confirm");
    const noteInput = overlay.querySelector('textarea[name="note"]');

    overlay.querySelector("#ar-note-prompt-cancel").addEventListener("click", () => close(false));
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(false); });
    document.addEventListener("keydown", onKey);

    form.addEventListener("submit", async (evt) => {
      evt.preventDefault();
      errorEl.hidden = true;
      const note = String(noteInput.value || "").trim().slice(0, maxLength);
      if (typeof onSubmit !== "function") {
        close(true);
        return;
      }
      await withButtonLoading(confirmBtn, async () => {
        try {
          await onSubmit(note);
          close(true);
        } catch (err) {
          errorEl.hidden = false;
          errorEl.textContent = err?.message || "Something went wrong.";
        }
      });
    });

    document.body.appendChild(overlay);
    noteInput.focus();
  });
}

/**
 * Branded "type a word to confirm" modal for destructive, hard-to-undo actions
 * (mirrors the delete-account flow). The confirm button stays disabled until the
 * user types `confirmWord`. If `onSubmit` is provided it runs inside the button's
 * loading state; a throw is shown inline and the modal stays open for a retry.
 * Resolves true once confirmed (and onSubmit succeeds), false on cancel/Escape.
 */
export function showTypeToConfirmModal({
  title = "Are you sure?",
  message = "",
  confirmWord = "delete",
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  // A way out offered before the word is typed, as Delete account offers the
  // export first: { note, label, onClick }. Taking it closes this.
  aside = null,
  onSubmit
} = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "ar-confirm-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "ar-type-confirm-title");
    overlay.innerHTML = `
      <div class="ar-confirm-modal">
        <h2 class="ar-confirm-modal-title" id="ar-type-confirm-title">${escapeHtml(title)}</h2>
        ${message ? `<p class="ar-confirm-modal-message">${escapeHtml(message)}</p>` : ""}
        ${aside ? `
        <p class="ar-confirm-modal-message">${escapeHtml(aside.note)}</p>
        <div class="ar-confirm-modal-actions ar-delete-account-export">
          <button class="ar-btn ar-btn-secondary" id="ar-type-confirm-aside" type="button">${escapeHtml(aside.label)}</button>
        </div>` : ""}
        <form id="ar-type-confirm-form" novalidate>
          <label class="app-profile-drawer-field app-profile-drawer-field-full">
            <span class="app-form-label">Type <strong>${escapeHtml(confirmWord)}</strong> to confirm</span>
            <div class="ar-input-spec-field-wrap">
              <input class="ar-input-spec-control" type="text" name="confirmation" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" required />
            </div>
          </label>
          <p class="ar-input-spec-error-text" id="ar-type-confirm-error" hidden></p>
          <div class="ar-confirm-modal-actions">
            <button class="ar-btn ar-btn-secondary" id="ar-type-confirm-cancel" type="button">${escapeHtml(cancelLabel)}</button>
            <button class="ar-btn ar-btn-danger-primary" id="ar-type-confirm-confirm" type="submit" disabled>${escapeHtml(confirmLabel)}</button>
          </div>
        </form>
      </div>
    `;

    function close(result) {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve(result);
    }

    function onKey(e) {
      if (e.key === "Escape") close(false);
    }

    const form = overlay.querySelector("#ar-type-confirm-form");
    const errorEl = overlay.querySelector("#ar-type-confirm-error");
    const cancelBtn = overlay.querySelector("#ar-type-confirm-cancel");
    const confirmBtn = overlay.querySelector("#ar-type-confirm-confirm");
    const confirmInput = overlay.querySelector('input[name="confirmation"]');

    const isValid = () => confirmInput.value.trim().toLowerCase() === confirmWord.toLowerCase();

    cancelBtn.addEventListener("click", () => close(false));
    overlay.querySelector("#ar-type-confirm-aside")?.addEventListener("click", () => {
      close(false);
      aside.onClick();
    });
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(false); });
    document.addEventListener("keydown", onKey);

    confirmInput.addEventListener("input", () => {
      confirmBtn.disabled = !isValid();
      errorEl.hidden = true;
    });

    form.addEventListener("submit", async (evt) => {
      evt.preventDefault();
      if (!isValid()) {
        errorEl.hidden = false;
        errorEl.textContent = `Type “${confirmWord}” to confirm.`;
        return;
      }
      errorEl.hidden = true;
      if (typeof onSubmit !== "function") {
        close(true);
        return;
      }
      await withButtonLoading(confirmBtn, async () => {
        try {
          await onSubmit();
          close(true);
        } catch (err) {
          errorEl.hidden = false;
          errorEl.textContent = err?.message || "Something went wrong.";
        }
      });
    });

    document.body.appendChild(overlay);
    confirmInput.focus();
  });
}

/**
 * Confirmation dialog with a date field — for actions that need a day chosen
 * before they run (scheduling an invoice send). Reuses the standard dialog
 * chrome; the date field is the DS date input, mounted into the dialog rather
 * than re-implemented, so it validates and formats exactly as it does in a
 * form.
 *
 * This replaced an inline disclosure row under the compose actions, which put
 * a "Schedule send" button and a "Schedule" button on screen at the same time
 * and read as a permanent field rather than a transient choice.
 *
 * `onSubmit(date)` runs inside the confirm button's loading state; a throw is
 * shown inline and the dialog stays open for a retry. Resolves true once
 * confirmed (and onSubmit succeeds), false on cancel / Escape / backdrop.
 */
export function showDatePromptModal({
  title = "Choose a date",
  message = "",
  dateLabel = "Date",
  value = "",
  min = "",
  max = "",
  hint = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onSubmit
} = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "ar-confirm-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "ar-date-prompt-title");
    overlay.innerHTML = `
      <div class="ar-confirm-modal">
        <h2 class="ar-confirm-modal-title" id="ar-date-prompt-title">${escapeHtml(title)}</h2>
        ${message ? `<p class="ar-confirm-modal-message">${escapeHtml(message)}</p>` : ""}
        <form id="ar-date-prompt-form" novalidate>
          <div id="ar-date-prompt-host"></div>
          <p class="ar-input-spec-error-text" id="ar-date-prompt-error" hidden></p>
          <div class="ar-confirm-modal-actions">
            <button class="ar-btn ar-btn-secondary" id="ar-date-prompt-cancel" type="button">${escapeHtml(cancelLabel)}</button>
            <button class="ar-btn ar-btn-primary" id="ar-date-prompt-confirm" type="submit">${escapeHtml(confirmLabel)}</button>
          </div>
        </form>
      </div>
    `;

    function close(result) {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve(result);
    }

    function onKey(e) {
      if (e.key === "Escape") close(false);
    }

    const form = overlay.querySelector("#ar-date-prompt-form");
    const errorEl = overlay.querySelector("#ar-date-prompt-error");
    const confirmBtn = overlay.querySelector("#ar-date-prompt-confirm");

    overlay.querySelector("#ar-date-prompt-cancel").addEventListener("click", () => close(false));
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(false); });
    document.addEventListener("keydown", onKey);

    document.body.appendChild(overlay);

    // Mounted after the overlay is in the document so the input can measure
    // and focus itself normally.
    const dateCtl = mountDateInput(overlay.querySelector("#ar-date-prompt-host"), {
      name: "promptDate",
      label: dateLabel,
      value,
      min,
      max,
      hint,
      required: true,
      error: false
    });

    form.addEventListener("submit", async (evt) => {
      evt.preventDefault();
      errorEl.hidden = true;
      const date = dateCtl?.getValue?.() || "";
      if (!date) {
        errorEl.textContent = "Choose a date.";
        errorEl.hidden = false;
        return;
      }
      if (typeof onSubmit !== "function") {
        close(true);
        return;
      }
      await withButtonLoading(confirmBtn, async () => {
        try {
          await onSubmit(date);
          close(true);
        } catch (err) {
          errorEl.textContent = err?.message || "Something went wrong. Please try again.";
          errorEl.hidden = false;
        }
      });
    });
  });
}

// Prompt for a money amount, pre-filled with the maximum and capped at it.
//
// Used by "Refund online payment", where the amount is the whole decision: the
// field opens on the full refundable figure so the common case (refund it all)
// is one click, and typing less makes it partial. `max` is enforced here as
// well as server-side so a typo is caught before it reaches Stripe.
//
// Deliberately no note field — the refund endpoint doesn't accept one; it
// writes its own "Refunded via Stripe" against the reversing payment.
export function showAmountPromptModal({
  title = "Amount",
  message = "",
  amountLabel = "Amount",
  value = "",
  max = null,
  hint = "",
  overMaxError = "That's more than you can refund.",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  onSubmit
} = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "ar-confirm-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "ar-amount-prompt-title");
    overlay.innerHTML = `
      <div class="ar-confirm-modal">
        <h2 class="ar-confirm-modal-title" id="ar-amount-prompt-title">${escapeHtml(title)}</h2>
        ${message ? `<p class="ar-confirm-modal-message">${escapeHtml(message)}</p>` : ""}
        <form id="ar-amount-prompt-form" novalidate>
          ${buildTextInput({
            label: amountLabel,
            name: "promptAmount",
            type: "text",
            value: String(value ?? ""),
            hint,
            widthModifier: "full",
            error: false,
            attrs: { inputmode: "decimal", autocomplete: "off" }
          })}
          <p class="ar-input-spec-error-text" id="ar-amount-prompt-error" hidden></p>
          <div class="ar-confirm-modal-actions">
            <button class="ar-btn ar-btn-secondary" id="ar-amount-prompt-cancel" type="button">${escapeHtml(cancelLabel)}</button>
            <button class="ar-btn ${danger ? "ar-btn-danger-primary" : "ar-btn-primary"}" id="ar-amount-prompt-confirm" type="submit">${escapeHtml(confirmLabel)}</button>
          </div>
        </form>
      </div>
    `;

    function close(result) {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve(result);
    }
    function onKey(e) { if (e.key === "Escape") close(false); }

    const form = overlay.querySelector("#ar-amount-prompt-form");
    const errorEl = overlay.querySelector("#ar-amount-prompt-error");
    const confirmBtn = overlay.querySelector("#ar-amount-prompt-confirm");
    const input = overlay.querySelector('input[name="promptAmount"]');

    overlay.querySelector("#ar-amount-prompt-cancel").addEventListener("click", () => close(false));
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(false); });
    document.addEventListener("keydown", onKey);
    document.body.appendChild(overlay);
    input?.focus();
    input?.select();

    form.addEventListener("submit", async (evt) => {
      evt.preventDefault();
      errorEl.hidden = true;
      const raw = String(input?.value || "").trim().replace(/[£,\s]/g, "");
      const amount = Number.parseFloat(raw);
      if (!raw) {
        errorEl.textContent = "Enter an amount.";
        errorEl.hidden = false;
        return;
      }
      if (!Number.isFinite(amount) || amount <= 0) {
        errorEl.textContent = "Enter an amount greater than zero.";
        errorEl.hidden = false;
        return;
      }
      if (max != null && amount > Number(max) + 1e-9) {
        errorEl.textContent = overMaxError;
        errorEl.hidden = false;
        return;
      }
      if (typeof onSubmit !== "function") { close(true); return; }
      await withButtonLoading(confirmBtn, async () => {
        try {
          await onSubmit(amount);
          close(true);
        } catch (err) {
          errorEl.textContent = err?.message || "Something went wrong. Please try again.";
          errorEl.hidden = false;
        }
      });
    });
  });
}
